import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ClipboardCheck,
  FileCheck,
  FileSpreadsheet,
  Inbox,
  FolderTree,
  KeyRound,
  Layers,
  Lock,
  Mail,
  MessageSquare,
  MessageSquareText,
  Map as MapIcon,
  Palette,
  ScrollText,
  ShieldCheck,
  Tags,
  Users,
} from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";

/**
 * Phoenix Capital admin hub. Landing page at /admin that surfaces every
 * platform-configuration tool in one place — replaces the previous
 * "NOT FOUND" state that the breadcrumb Admin link landed on.
 *
 * Cards are filtered to the permissions the current user actually holds,
 * so a Users.Read-only admin doesn't see Roles & Permissions dangling.
 */
interface AdminCard {
  key: string;
  title: string;
  description: string;
  route: string;
  icon: LucideIcon;
  requiredPermission: string;
}

const CARDS: AdminCard[] = [
  {
    key: "territories",
    title: "Territories",
    description: "Geographic routing: states, territories, and liaison assignments.",
    route: "/admin/territories",
    icon: MapIcon,
    requiredPermission: "Territories.Manage",
  },
  {
    key: "users",
    title: "Users",
    description: "Invite new users, update profiles, and manage role assignments.",
    route: "/admin/users",
    icon: Users,
    requiredPermission: "Users.Read",
  },
  {
    key: "admin-roles",
    title: "Roles",
    description: "Edit role codes, display names, and ordering. Archive retired roles without deleting historical references.",
    route: "/admin/roles",
    icon: ShieldCheck,
    requiredPermission: "Lookups.ManageRoles",
  },
  {
    key: "admin-categories",
    title: "Categories",
    description: "Edit report category codes and labels. Archived categories disappear from new-template dropdowns; existing templates keep working.",
    route: "/admin/categories",
    icon: Tags,
    requiredPermission: "Lookups.ManageCategories",
  },
  {
    key: "roles-permissions",
    title: "Roles & Permissions",
    description: "Edit the permission set granted to each role code.",
    route: "/admin/roles-permissions",
    icon: KeyRound,
    requiredPermission: "System.Configure",
  },
  {
    key: "qualification-fields",
    title: "Qualification Fields",
    description: "Checkboxes shown on the broker qualification form.",
    route: "/admin/qualification-fields",
    icon: ClipboardCheck,
    requiredPermission: "System.Configure",
  },
  {
    key: "report-templates",
    title: "Report Templates",
    description: "Unified report templates — prompt body, role assignments, optional master document, and the embedded form.",
    route: "/admin/report-templates",
    icon: FileSpreadsheet,
    requiredPermission: "Reports.ManageTemplates",
  },
  {
    key: "prompts",
    title: "Prompts",
    description: "Centralized AI prompt configuration — the unified prompt store, in-code catalog defaults, and reconciliation against the legacy prompt tables.",
    route: "/admin/prompts",
    icon: ScrollText,
    requiredPermission: "Prompts.Manage",
  },
  {
    key: "email-templates",
    title: "Email Templates",
    description:
      "The copy of every outbound email. Edit a subject or body block, preview it, and publish — every publish is versioned and can be rolled back.",
    route: "/admin/email-templates",
    icon: Mail,
    // The permission string, not a shared constant — there is no TS mirror of
    // Permissions.cs, so this must match Communications.ManageEmailTemplates
    // exactly or the tile renders for nobody.
    requiredPermission: "Communications.ManageEmailTemplates",
  },
  {
    key: "interview-wording",
    title: "Interview Wording",
    description:
      "The wording of the buyer qualification interview — questions, hints, choices and section text. Reword, preview and publish; keys and scoring never change.",
    route: "/admin/interview-wording",
    icon: MessageSquareText,
    // Must match Permissions.Buyers.ManageInterviewWording exactly (no TS
    // mirror of Permissions.cs).
    requiredPermission: "Buyers.ManageInterviewWording",
  },
  {
    key: "document-request-templates",
    title: "Document Request Templates",
    description:
      "Master catalog of Pre-Approval document requests. Add, edit, or deactivate request types; changes affect new deals only.",
    route: "/admin/document-request-templates",
    icon: FileCheck,
    requiredPermission: "DocumentRequestTemplates.Manage",
  },
  {
    key: "collection-templates",
    title: "Collection Templates",
    description:
      "External Intake & Collection — authoring for Form / Upload / E-Sign templates and the two AI prompts per template.",
    route: "/admin/collection-templates",
    icon: Inbox,
    requiredPermission: "Collection.ManageTemplates",
  },
  {
    key: "collection-bundles",
    title: "Collection Bundles",
    description:
      "Reusable, audience-tagged groups of collection templates. The composer's 'Add from group' instantiates every slot in one click.",
    route: "/admin/collection-bundles",
    icon: Inbox,
    requiredPermission: "Collection.ManageTemplates",
  },
  {
    key: "folder-templates",
    title: "Folder Templates",
    description: "Default folder tree created for every new entity.",
    route: "/admin/folder-templates",
    icon: FolderTree,
    requiredPermission: "Documents.ManageTemplates",
  },
  {
    key: "brand-identity",
    title: "Brand Identity",
    description: "Logo, colors, typography, and the branding guide Claude uses on every generation.",
    route: "/admin/brand-identity",
    icon: Palette,
    requiredPermission: "System.ManageBrandSettings",
  },
  {
    key: "audit",
    title: "Audit Log",
    description: "Append-only trail of every create / update / delete across the platform.",
    route: "/admin/audit",
    icon: ScrollText,
    requiredPermission: "System.AuditView",
  },
  // Knowledge Base v1.0 PR 4 — four admin surfaces (documents library,
  // categories, tags, folder-access matrix). All gated on the single
  // ManageDocuments permission so a manager sees the full set.
  {
    key: "kb-documents",
    title: "Knowledge Base · Documents",
    description: "Upload, edit, retire, and re-index the curated document library.",
    route: "/admin/knowledge-base-documents",
    icon: BookOpen,
    requiredPermission: "KnowledgeBase.ManageDocuments",
  },
  {
    key: "kb-categories",
    title: "Knowledge Base · Categories",
    description: "Top-level folders for the knowledge base. Pair with the access matrix to gate folders to positions.",
    route: "/admin/knowledge-base-categories",
    icon: Layers,
    requiredPermission: "KnowledgeBase.ManageDocuments",
  },
  {
    key: "kb-tags",
    title: "Knowledge Base · Tags",
    description: "Tags grouped by kind (audience, document type, theme, lifecycle).",
    route: "/admin/knowledge-base-tags",
    icon: Tags,
    requiredPermission: "KnowledgeBase.ManageDocuments",
  },
  {
    key: "kb-access",
    title: "Knowledge Base · Folder access",
    description: "Restrict knowledge base folders to specific positions. Empty rows are open to all.",
    route: "/admin/knowledge-base-access",
    icon: Lock,
    requiredPermission: "KnowledgeBase.ManageDocuments",
  },
  // Knowledge Base v1.0 PR 5 — read-only feedback review so admins can
  // spot corpus gaps from the NotHelpful filter.
  {
    key: "kb-queries",
    title: "Knowledge Base · Feedback review",
    description: "Recent questions, answers, citations, and thumbs up/down. Filter by Not helpful to find corpus gaps.",
    route: "/admin/knowledge-base-queries",
    icon: MessageSquare,
    requiredPermission: "KnowledgeBase.ManageDocuments",
  },
];

export function AdminHubPage() {
  const { hasPermission } = useAuth();
  const visible = CARDS.filter((c) => hasPermission(c.requiredPermission));

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Admin"
        description="Platform configuration and management tools."
      />

      {visible.length === 0 ? (
        <div className="text-caption text-gray-mid italic">
          No admin tools are available for your role.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-space-3">
          {visible.map((c) => (
            <Link
              key={c.key}
              to={c.route}
              aria-label={`Open ${c.title}`}
              className="group"
            >
              <Card className="p-space-4 h-full flex flex-col gap-space-2 border border-border-light hover:border-gold hover:shadow-sm transition-colors">
                <div className="flex items-center gap-space-2 text-gold-shadow group-hover:text-gold">
                  <c.icon size={20} strokeWidth={1.5} aria-hidden />
                  <h3 className="font-display font-bold text-h4 text-onyx m-0">{c.title}</h3>
                </div>
                <p className="text-small text-gray-dark leading-normal">{c.description}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
